import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Camera, ExternalLink } from "lucide-react";
import HlsPlayer from "@/components/HlsPlayer";

const getGameId = (url = "") => {
  const m = url.match(/\/full-replays\/(\d+)\//);
  return m ? m[1] : null;
};
const getPlayMomentId = (url = "") => {
  try {
    const path = new URL(url).pathname.split("/").filter(Boolean);
    if (path.length >= 2) return path[1];
  } catch { /* noop */ }
  return null;
};
const getDuration = (desc = "") => {
  const m = desc.match(/(\d+h\s*\d+m|\d+h|\d+m|\d+s)/);
  return m ? m[1].replace(/\s+/g, " ") : null;
};

export default function VideoDetailModal({ video, open, onClose }) {
  const [activeAngle, setActiveAngle] = useState(0);

  if (!video) return null;

  const currentAngle = video.angles?.[activeAngle] || video.angles?.[0];
  const firstUrl = video.angles?.[0]?.url || "";
  const isRaw = video.video_type === "raw_footage";
  const idLabel = isRaw
    ? (video.game_id || getGameId(firstUrl))
    : (video.play_moment_id || getPlayMomentId(firstUrl));
  const displayTitle = isRaw
    ? `Game: #${idLabel ?? "—"}`
    : `Clip: #${idLabel ?? "—"}`;
  const duration = video.duration || getDuration(video.description || "");

  const handleDownload = () => {
    if (currentAngle?.url) {
      window.open(currentAngle.url, "_blank");
    }
  };

  const isHlsUrl = (url) => url && url.includes(".m3u8");
  const isDirectVideo = (url) => url && url.match(/\.(mp4|webm|ogg|mov)(\?|$)/i);
  const isEmbedUrl = (url) => url && (url.includes("youtube.com") || url.includes("vimeo.com"));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] p-0 bg-white rounded-2xl overflow-hidden border-none" data-testid="video-detail-modal">
        <div className="p-4 md:p-6">
          {/* Header */}
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <DialogTitle className="text-xl md:text-2xl font-bold text-[#0A1128] truncate" style={{ fontFamily: 'Outfit' }}>
                  {displayTitle}
                </DialogTitle>
                <DialogDescription className="sr-only">Video detail viewer with multi-angle playback</DialogDescription>
                <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-[#0A1128]/60">
                  {duration && (
                    <>
                      <span className="font-mono tabular-nums">{duration}</span>
                      <span className="text-[#0A1128]/25">·</span>
                    </>
                  )}
                  <span className="bg-[#0A1128]/5 text-[#0A1128] px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                    {video.sport}
                  </span>
                  {!isRaw && video.sporting_moment && (
                    <>
                      <span className="text-[#0A1128]/25">·</span>
                      <span className="italic text-[#FF5A00] font-medium tracking-wide">
                        {video.sporting_moment}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Video Player */}
          <div className="video-player-container rounded-xl mb-4" data-testid="video-player">
            {currentAngle?.url ? (
              isHlsUrl(currentAngle.url) ? (
                <HlsPlayer key={currentAngle.url} src={currentAngle.url} autoPlay />
              ) : isDirectVideo(currentAngle.url) ? (
                <video
                  key={currentAngle.url}
                  controls
                  autoPlay
                  className="absolute inset-0 w-full h-full"
                  data-testid="video-element"
                >
                  <source src={currentAngle.url} />
                </video>
              ) : isEmbedUrl(currentAngle.url) ? (
                <iframe
                  key={currentAngle.url}
                  src={currentAngle.url}
                  title={displayTitle}
                  className="absolute inset-0 w-full h-full"
                  allowFullScreen
                  allow="autoplay; encrypted-media"
                  data-testid="video-iframe"
                />
              ) : (
                <HlsPlayer key={currentAngle.url} src={currentAngle.url} autoPlay />
              )
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/40">
                No video URL available
              </div>
            )}
          </div>

          {/* Angle Selector */}
          {video.angles && video.angles.length > 1 && (
            <div className="mb-4" data-testid="angle-selector">
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4 text-[#0A1128]/60" />
                <span className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Camera Angles</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {video.angles.map((angle, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveAngle(idx)}
                    data-testid={`angle-btn-${idx}`}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      idx === activeAngle
                        ? "bg-[#FF5A00] text-white shadow-sm"
                        : "bg-[#0A1128]/5 text-[#0A1128] hover:bg-[#0A1128]/10"
                    }`}
                  >
                    {angle.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Download */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleDownload}
              data-testid="download-btn"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-6"
              disabled={!currentAngle?.url}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            {currentAngle?.url && (
              <a
                href={currentAngle.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-external-btn"
                className="flex items-center gap-2 text-sm text-[#0A1128]/60 hover:text-[#FF5A00] transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open in new tab
              </a>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
